import { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from "react";

import { useTimelineStore } from "../store/timeline-context";
import {
  anchoredScrollLeft,
  clampPixelsPerSecond,
  fitPixelsPerSecond,
  maxScrollLeft,
  ZOOM_STEP_FACTOR,
  type TimelineZoom,
} from "../utils/viewport";

/**
 * Props for the useTimelineViewport hook
 */
type UseTimelineViewportProps = {
  /** Total duration of the timeline in seconds */
  durationSeconds: number;
  /** Total height of all rows combined in pixels */
  totalHeight: number;
};

/**
 * Manages timeline viewport dimensions, scroll state, and zoom level.
 *
 * This hook maintains viewport size, scroll position (horizontal and vertical),
 * and pixels-per-second zoom level. It calculates scroll bounds based on
 * content dimensions and provides setters for updating viewport state.
 *
 * @param props - Hook configuration
 * @returns Viewport state, scroll state, zoom level, and update functions
 */
export const useTimelineViewport = ({ durationSeconds, totalHeight }: UseTimelineViewportProps) => {
  const pixelsPerSecond = useTimelineStore((state) => state.pixelsPerSecond);
  const setPixelsPerSecond = useTimelineStore((state) => state.setPixelsPerSecond);

  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(1);
  const [viewportHeight, setViewportHeight] = useState(1);

  // Memoize to prevent recalculation on unrelated state changes
  const maxScroll = useMemo(
    () => maxScrollLeft(durationSeconds, pixelsPerSecond, viewportWidth),
    [durationSeconds, pixelsPerSecond, viewportWidth],
  );
  const maxScrollTop = useMemo(() => Math.max(0, totalHeight - viewportHeight), [totalHeight, viewportHeight]);

  // Content can shrink under the current scroll: a collapse, a wider viewport,
  // a shorter duration, or a refit. Clamp before paint so the empty space past
  // the end never shows.
  useLayoutEffect(() => {
    if (scrollLeft > maxScroll) {
      setScrollLeft(maxScroll);
    }
  }, [maxScroll, scrollLeft]);
  useLayoutEffect(() => {
    if (scrollTop > maxScrollTop) {
      setScrollTop(maxScrollTop);
    }
  }, [maxScrollTop, scrollTop]);

  const setViewport = useCallback((width: number, height: number) => {
    setViewportWidth(width);
    setViewportHeight(height);
  }, []);

  // Track the most recent fit-to-width zoom value we wrote so we can detect
  // whether the user has manually zoomed since. Read pixelsPerSecond via a ref
  // so wheel-driven zoom changes don't retrigger this effect, and so repeated
  // zoom commands within one frame start from the latest value.
  const lastFitZoomRef = useRef<number | null>(null);
  const pixelsPerSecondRef = useRef(pixelsPerSecond);
  pixelsPerSecondRef.current = pixelsPerSecond;

  // Initialize zoom to fit on first measurement, and resync on subsequent
  // viewport-width or duration changes — but only if the user hasn't manually
  // zoomed away from the previous fit value (e.g. via wheel/pinch).
  useEffect(() => {
    if (viewportWidth <= 1 || durationSeconds <= 0) return;

    const fitZoom = fitPixelsPerSecond(viewportWidth, durationSeconds);

    if (lastFitZoomRef.current === null) {
      setPixelsPerSecond(fitZoom);
      lastFitZoomRef.current = fitZoom;
      return;
    }

    const currentPx = pixelsPerSecondRef.current;
    const userHasZoomed = Math.abs(currentPx - lastFitZoomRef.current) > 0.001;
    if (userHasZoomed) return;

    if (Math.abs(currentPx - fitZoom) > 0.001) {
      setPixelsPerSecond(fitZoom);
    }
    lastFitZoomRef.current = fitZoom;
  }, [viewportWidth, durationSeconds, setPixelsPerSecond]);

  /**
   * Step the zoom around `anchorX` (viewport px), or fit the whole duration.
   * Fitting also re-arms the follow-the-viewport refit above.
   */
  const zoom = useCallback(
    (command: TimelineZoom, anchorX: number) => {
      if (viewportWidth <= 1 || durationSeconds <= 0) return;
      const from = pixelsPerSecondRef.current;
      if (command === "fit") {
        const fitZoom = fitPixelsPerSecond(viewportWidth, durationSeconds);
        lastFitZoomRef.current = fitZoom;
        pixelsPerSecondRef.current = fitZoom;
        setPixelsPerSecond(fitZoom);
        setScrollLeft(0);
        return;
      }
      const to = clampPixelsPerSecond(
        command === "in" ? from * ZOOM_STEP_FACTOR : from / ZOOM_STEP_FACTOR,
        viewportWidth,
        durationSeconds,
      );
      if (to === from) return;
      pixelsPerSecondRef.current = to;
      setPixelsPerSecond(to);
      setScrollLeft((current) =>
        anchoredScrollLeft({
          scrollLeft: current,
          fromPixelsPerSecond: from,
          toPixelsPerSecond: to,
          anchorX,
          durationSeconds,
          viewportWidth,
        }),
      );
    },
    [durationSeconds, viewportWidth, setPixelsPerSecond],
  );

  return {
    pixelsPerSecond,
    setPixelsPerSecond,
    scrollLeft,
    setScrollLeft,
    scrollTop,
    setScrollTop,
    viewportWidth,
    viewportHeight,
    maxScroll,
    maxScrollTop,
    setViewport,
    zoom,
  };
};
